<template>
  <AuthLayout :mode="mode">
    <Section
      heading="Manage Listings"
      description="This page allows you to manage your listings. Use the buttons below to either delete or re-list all of you listings. You can also use this page to search for your OWN listings in order to see, update or remove your listings. Leave the box below empty and click on SEARCH to see all of your listings OR enter criteria into the box to narrow down your search."
      :mode="mode"
    />
    <AppCard :mode="mode">
      <template #body>
        <div class="mb-6 flex items-center justify-between gap-4 sm:flex-col">
          <div class="flex-1 sm:w-full">
            <div class="relative flex items-center">
              <Search
                :size="20"
                :stroke-width="1.75"
                class="absolute left-4 opacity-40"
              />
              <AppInput
                :mode="mode"
                v-model="searchQuery"
                type="search"
                placeholder="Search for your listings"
                classes="!pl-11"
                :hasLabel="false"
              />
            </div>
          </div>
          <div class="flex w-[280px] gap-2 sm:grid sm:w-full sm:gap-4">
            <AppButton
              title="Re-list all"
              @action="relistAllListings"
              :isDisabled="listings.length === 0"
            />
            <AppButton
              title="Delete all"
              @action="deleteAllListings"
              :isDisabled="listings.length === 0"
            />
          </div>
        </div>
        <template v-if="paginatedItems.length > 0">
          <AppTable :mode="mode" :columns="columns">
            <template #rows>
              <tr v-for="(listing, key) in paginatedItems" :key="key">
                <td class="align-top">
                  <FallbackText :value="listing.listing.listing_id" />
                </td>
                <td class="align-top">
                  <FallbackDateTime
                    :mode="mode"
                    :value="listing.listing.post_date"
                  />
                </td>
                <td class="align-top">
                  <div class="grid gap-1">
                    <div
                      class="text-[14px] font-medium"
                      :class="[
                        mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                      ]"
                    >
                      <span
                        class="font-semibold"
                        :class="[
                          mode === 'dark' ? 'text-white-100' : 'text-black-80',
                        ]"
                        >Mfr:</span
                      >
                      {{ listing.listing.mfr ? listing.listing.mfr : "-" }}
                    </div>
                    <div
                      class="text-[14px] font-medium"
                      :class="[
                        mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                      ]"
                    >
                      <span
                        class="font-semibold"
                        :class="[
                          mode === 'dark' ? 'text-white-100' : 'text-black-80',
                        ]"
                        >Machine:</span
                      >
                      {{
                        listing.listing.machine ? listing.listing.machine : "-"
                      }}
                    </div>
                    <div
                      class="text-[14px] font-medium"
                      :class="[
                        mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                      ]"
                    >
                      <span
                        class="font-semibold"
                        :class="[
                          mode === 'dark' ? 'text-white-100' : 'text-black-80',
                        ]"
                        >Model:</span
                      >
                      {{ listing.listing.model ? listing.listing.model : "-" }}
                    </div>
                    <div
                      class="text-[14px] font-medium"
                      :class="[
                        mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                      ]"
                    >
                      <span
                        class="font-semibold"
                        :class="[
                          mode === 'dark' ? 'text-white-100' : 'text-black-80',
                        ]"
                        >Comment:</span
                      >
                      {{
                        listing.listing.comments
                          ? listing.listing.comments
                          : "-"
                      }}
                    </div>
                    <div
                      class="text-[14px] font-medium"
                      :class="[
                        mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                      ]"
                    >
                      <span
                        class="font-semibold"
                        :class="[
                          mode === 'dark' ? 'text-white-100' : 'text-black-80',
                        ]"
                        >Location:</span
                      >
                      {{
                        listing.listing.location
                          ? listing.listing.location
                          : "-"
                      }}
                    </div>
                  </div>
                </td>
                <td class="align-top">
                  <div class="flex items-center gap-2">
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo(
                          'listing-details',
                          listing.listing.listing_id,
                        )
                      "
                      action="view"
                    />
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo('listing-edit', listing.listing.listing_id)
                      "
                      action="edit"
                    />
                    <TableAction
                      :mode="mode"
                      @itemAction="deleteListing(listing.listing.listing_id)"
                      action="delete"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </AppTable>
          <MobileTable
            :mode="mode"
            :fields="{
              id: 'ID',
              post_date: 'Post date',
              details: 'Details',
              actions: 'Actions',
            }"
            :data="paginatedItems"
          >
            <template #default="{ row, fieldKey }">
              <template v-if="fieldKey === 'id'">
                <FallbackText
                  :value="row.listing.listing_id"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'post_date'">
                <FallbackDateTime :mode="mode" :value="row.listing.post_date" />
              </template>
              <template v-else-if="fieldKey === 'details'">
                <div class="grid gap-1">
                  <div
                    class="text-[14px] font-medium"
                    :class="[
                      mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                    ]"
                  >
                    <span
                      class="font-semibold"
                      :class="[
                        mode === 'dark' ? 'text-white-100' : 'text-black-80',
                      ]"
                      >Mfr:</span
                    >
                    {{ row.listing.mfr ? row.listing.mfr : "-" }}
                  </div>
                  <div
                    class="text-[14px] font-medium"
                    :class="[
                      mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                    ]"
                  >
                    <span
                      class="font-semibold"
                      :class="[
                        mode === 'dark' ? 'text-white-100' : 'text-black-80',
                      ]"
                      >Machine:</span
                    >
                    {{ row.listing.machine ? row.listing.machine : "-" }}
                  </div>
                  <div
                    class="text-[14px] font-medium"
                    :class="[
                      mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                    ]"
                  >
                    <span
                      class="font-semibold"
                      :class="[
                        mode === 'dark' ? 'text-white-100' : 'text-black-80',
                      ]"
                      >Model:</span
                    >
                    {{ row.listing.model ? row.listing.model : "-" }}
                  </div>
                  <div
                    class="text-[14px] font-medium"
                    :class="[
                      mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                    ]"
                  >
                    <span
                      class="font-semibold"
                      :class="[
                        mode === 'dark' ? 'text-white-100' : 'text-black-80',
                      ]"
                      >Comment:</span
                    >
                    {{ row.listing.comments ? row.listing.comments : "-" }}
                  </div>
                  <div
                    class="text-[14px] font-medium"
                    :class="[
                      mode === 'dark' ? 'text-dark-text' : 'text-black-60',
                    ]"
                  >
                    <span
                      class="font-semibold"
                      :class="[
                        mode === 'dark' ? 'text-white-100' : 'text-black-80',
                      ]"
                      >Location:</span
                    >
                    {{ row.listing.location ? row.listing.location : "-" }}
                  </div>
                </div>
              </template>
              <template v-else-if="fieldKey === 'actions'">
                <div class="flex items-center gap-2">
                  <TableAction
                    :mode="mode"
                    @itemAction="
                      $redirectTo('listing-details', row.listing.listing_id)
                    "
                    action="view"
                  />
                  <TableAction
                    :mode="mode"
                    @itemAction="
                      $redirectTo('listing-edit', row.listing.listing_id)
                    "
                    action="edit"
                  />
                  <TableAction
                    :mode="mode"
                    @itemAction="deleteListing(row.listing.listing_id)"
                    action="delete"
                  />
                </div>
              </template>
            </template>
          </MobileTable>
        </template>
        <EmptyData
          v-else
          :notFound="'No listing represented!'"
          @clear="clear"
          :isClear="true"
          :mode="mode"
        />
        <AppPagination
          :mode="mode"
          :data="listings"
          @pageChanged="updatePaginatedItems"
        />
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import { Search } from "lucide-vue-next";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import EmptyData from "@C/Common/EmptyData.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AppPagination from "@C/Common/AppPagination.vue";
import AuthLayout from "@A/AuthLayout.vue";
import FallbackDateTime from "@C/Tables/FallbackDateTime.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import AppButton from "@C/Common/AppButton.vue";
import TableAction from "@C/Tables/TableAction.vue";

export default {
  name: "ManageListing",
  components: {
    AuthLayout,
    Section,
    Search,
    EmptyData,
    AppInput,
    AppCard,
    AppTable,
    AppPagination,
    TableAction,
    FallbackDateTime,
    FallbackText,
    MobileTable,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      listings: [],
      searchQuery: "",
      debounceTimer: null,
      columns: [
        {
          name: "ID",
        },
        {
          name: "Post Date",
        },
        {
          name: "Details",
        },
        {
          name: "Actions",
          classes: "w-[80px]",
        },
      ],
      paginatedItems: [],
    };
  },
  mounted() {
    this.getListings();
  },
  methods: {
    async getListings() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/listing`);
        if (data) {
          this.listings = data;
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async deleteListing(id) {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().delete(`/listing/${id}`);
        if (data) {
          this.toastStore.show("Listing deleted successfully!", "success");
          this.getListings();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    searchListings() {
      if (this.searchQuery.trim() === "") {
        this.getListings();
      } else {
        this.getSearchQueryLising();
      }
    },
    async getSearchQueryLising() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        comments: this.searchQuery,
        machine: this.searchQuery,
        model: this.searchQuery,
      };
      try {
        const { data } = await ApiRequest().post(`/listing_search`, payload);
        if (data) {
          this.listings = data.map((listing) => listing._source);
        } else {
          this.listings = [];
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async relistAllListings() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/listing_relist_all`);
        if (data) {
          this.toastStore.show(
            "All listings re-listed successfully!",
            "success",
          );
          this.getListings();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async deleteAllListings() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/listing_delete_all`);
        if (data) {
          this.toastStore.show("All listings deleted successfully!", "success");
          this.getListings();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    clear() {
      this.searchQuery = "";
      this.getListings();
    },
    updatePaginatedItems(paginatedData) {
      this.paginatedItems = paginatedData;
    },
  },
  watch: {
    searchQuery() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.searchListings();
      }, 300);
    },
  },
};
</script>
